import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { BRAND, NAV, wa } from "@/lib/site";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.5c-1.3.1-2.5-.3-3.7-1v6.1c0 3.4-2.6 5.8-5.9 5.5-2.7-.2-4.8-2.5-4.7-5.2.1-2.7 2.4-4.8 5.1-4.7.3 0 .6 0 .9.1v2.6c-.3-.1-.6-.2-.9-.2-1.2 0-2.2 1-2.2 2.2s1 2.2 2.2 2.2 2.2-1 2.2-2.2V3h3.3z" />
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const quoteMsg =
    "Halo HATTORY KONVEKSI, saya ingin meminta penawaran untuk kebutuhan konveksi custom.";

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="hidden bg-navy text-white md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 text-xs md:px-12">
          <div className="flex items-center gap-6 text-silver">
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="topbar-phone">
              <Phone className="h-3.5 w-3.5" /> {BRAND.phone}
            </a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="topbar-email">
              <Mail className="h-3.5 w-3.5" /> {BRAND.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href={BRAND.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-silver transition-colors hover:text-white"><Instagram className="h-4 w-4" /></a>
            <a href={BRAND.socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-silver transition-colors hover:text-white"><TikTokIcon className="h-4 w-4" /></a>
            <a href={BRAND.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-silver transition-colors hover:text-white"><Facebook className="h-4 w-4" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`border-b transition-colors duration-300 ${scrolled ? "border-silver/20 bg-white/85 backdrop-blur-xl" : "border-transparent bg-white/70 backdrop-blur-md"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={BRAND.logo} alt="HATTORY KONVEKSI" className="h-9 w-9 object-contain" />
            <span className="text-xl font-black tracking-tight text-navy">HATTORY</span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => {
              const active = item.to === pathname;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative text-sm font-medium transition-colors ${active ? "text-navy" : "text-charcoal/70 hover:text-navy"}`}
                >
                  {item.label}
                  {active && (
                    <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 h-0.5 w-full bg-navy" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={wa(quoteMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-quote-btn"
              className="hidden rounded-full bg-navy px-6 py-3 text-sm font-medium text-white transition-transform duration-300 hover:scale-105 active:scale-95 sm:inline-flex"
            >
              Minta Penawaran
            </a>
            <button
              className="lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Buka menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6 text-navy" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 26, stiffness: 220 }}
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-sm flex-col bg-white p-6"
            >
              <div className="mb-8 flex items-center justify-between">
                <span className="text-lg font-black text-navy">HATTORY</span>
                <button onClick={() => setOpen(false)} aria-label="Tutup menu" data-testid="mobile-close-btn">
                  <X className="h-6 w-6 text-navy" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-xl px-4 py-3 text-lg font-medium transition-colors ${item.to === pathname ? "bg-surface text-navy" : "text-charcoal hover:bg-surface"}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <a
                href={wa(quoteMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 rounded-full bg-navy px-6 py-4 text-center font-medium text-white"
                data-testid="mobile-quote-btn"
              >
                Minta Penawaran
              </a>
              <div className="mt-auto border-t border-silver/30 pt-6 text-sm text-charcoal/70">
                <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {BRAND.phone}</p>
                <p className="mt-2 flex items-center gap-2"><Mail className="h-4 w-4" /> {BRAND.email}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
